import { TpMatrixColumnType } from '../../etl-data-types/interfaces';

interface TpSummaryStatisticType {
  id: string;
  name: string;
  columnName: string;
  value: boolean; // Indicates if the statistic requires a value
  description: string;
}

export const summaryTypes: TpSummaryStatisticType[] = [
  {
    id: 'sum',
    name: 'Sum',
    columnName: 'Sum of the values',
    value: false,
    description: 'Sum of the values across the specified dimension',
  },
  {
    id: 'mean',
    name: 'Mean',
    columnName: 'Mean values',
    value: false,
    description: 'Mean of the values across the specified dimension',
  },
  {
    id: 'median',
    name: 'Median',
    columnName: 'Median values',
    value: false,
    description: 'Median of the values across the specified dimension',
  },
  {
    id: 'stddev',
    name: 'Standard deviation',
    columnName: 'Standard deviations',
    value: false,
    description: 'Standard deviation of the values across the specified dimension',
  },
  {
    id: 'min',
    name: 'Minimum',
    columnName: 'Minimum values',
    value: false,
    description: 'Minimum value across the specified dimension',
  },
  {
    id: 'max',
    name: 'Maximum',
    columnName: 'Maximum values',
    value: false,
    description: 'Maximum value across the specified dimension',
  },
  {
    id: 'meanlog',
    name: 'Value mean logs',
    columnName: 'Mean of the logs',
    value: false,
    description: 'Mean of the log-transformed values across the specified dimension',
  },
  {
    id: 'stderr',
    name: 'Standard error',
    columnName: 'Standard errors',
    value: false,
    description: 'Standard error of the values across the specified dimension',
  },
  {
    id: 'count_na',
    name: 'Count NA',
    columnName: 'Values == NA',
    value: false,
    description: 'Number of NA values across the specified dimension',
  },
  {
    id: 'fraction_na',
    name: 'Fraction NA',
    columnName: 'Fraction == NA',
    value: false,
    description: 'Fraction of NA values across the specified dimension',
  },
  {
    id: 'count_eq',
    name: 'Count equals (==)',
    columnName: 'Values ==',
    value: true,
    description: 'Count of values equal (==) to a specified value',
  },
  {
    id: 'count_neq',
    name: 'Count not equals (!=)',
    columnName: 'Values !=',
    value: true,
    description: 'Count of values not equal (!=) to a specified value',
  },
  {
    id: 'count_gt',
    name: 'Count greater than (>)',
    columnName: 'Values >',
    value: true,
    description: 'Count of values greater than (>) a specified value',
  },
  {
    id: 'count_gte',
    name: 'Count greater than or equals (>=)',
    columnName: 'Values >=',
    value: true,
    description: 'Count of values greater than or equals to (>=) a specified value',
  },
  {
    id: 'count_lt',
    name: 'Count less than (<)',
    columnName: 'Values <',
    value: true,
    description: 'Count of values less than (<) a specified value',
  },
  {
    id: 'count_lte',
    name: 'Count less than or equals (<=)',
    columnName: 'Values <=',
    value: true,
    description: 'Number of values less than or equals to (<=) a specified value',
  },
  {
    id: 'fraction_eq',
    name: 'Fraction equals (==)',
    columnName: 'Fraction ==',
    value: true,
    description: 'Fraction of values equal (==) to a specified value',
  },
  {
    id: 'fraction_neq',
    name: 'Fraction not equals (!=)',
    columnName: 'Fraction !=',
    value: true,
    description: 'Fraction of values not equal (!=) to a specified value',
  },
  {
    id: 'fraction_gt',
    name: 'Fraction greater than (>)',
    columnName: 'Fraction >',
    value: true,
    description: 'Fraction of values greater than (>) a specified value',
  },
  {
    id: 'fraction_gte',
    name: 'Fraction greater than or equals (>=)',
    columnName: 'Fraction >=',
    value: true,
    description: 'Fraction of values greater than or equals to (>=) a specified value',
  },
  {
    id: 'fraction_lt',
    name: 'Fraction less than (<)',
    columnName: 'Fraction <',
    value: true,
    description: 'Fraction of values less than (<) a specified value',
  },
  {
    id: 'fraction_lte',
    name: 'Fraction less than or equals (<=)',
    columnName: 'Fraction <=',
    value: true,
    description: 'Fraction of values less than or equal to (<=) a specified value',
  },
];

export function getSummaryTypeById(id: string) {
  return summaryTypes.find((type) => type.id === id);
}

export function getSummaryTypeColumnName(
  summaryTypeId: string,
  dimension: TpMatrixColumnType | '',
  summaryValue?: number,
): string {
  const summaryType = summaryTypes.find((type) => type.id === summaryTypeId);
  if (!summaryType) return summaryTypeId; // Return the ID if not found
  let name = summaryType.columnName;
  if (dimension !== '') {
    name = name.replace('specified dimension', dimension);
  }
  if (summaryType.value && summaryValue !== undefined) {
    name = `${name} ${summaryValue}`;
  }
  return name;
}

export function getSummaryTypeDescription(
  summaryTypeId: string,
  dimension: TpMatrixColumnType | '',
  summaryValue?: number,
): string {
  const summaryType = summaryTypes.find((type) => type.id === summaryTypeId);
  if (!summaryType) return summaryTypeId; // Return the ID if not found
  let description = summaryType.description;
  if (dimension !== '') {
    description = description.replace('specified dimension', dimension);
  }
  if (summaryType.value && summaryValue !== undefined) {
    description = description.replace('a specified value', summaryValue.toString());
  }
  return description;
}
